import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Briefcase, Building2, Users, CheckCircle, Search, ArrowRight, MapPin, TrendingUp, Code, HeartPulse, ShoppingBag, Factory, GraduationCap, Hammer, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { statsAPI, jobsAPI } from '../lib/api';

// --- COMPONENTE: CONTADOR ANIMADO ---
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const countRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (countRef.current) observer.observe(countRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;
    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [isVisible, end, duration]);

  return <span ref={countRef}>{count.toLocaleString('pt-BR')}{suffix}</span>;
};

// --- STAT CARD (Responsivo + Animado) ---
const StatCard = ({ icon: Icon, value, label, gradient, delay = 0 }) => (
  <div 
    className={`group relative overflow-hidden rounded-3xl p-5 md:p-6 transition-all duration-500 hover:scale-[1.03] hover:shadow-2xl animate-in fade-in slide-in-from-bottom-4 fill-mode-forwards ${gradient}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative z-10 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-3 md:gap-4">
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform duration-500 shadow-inner">
        <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={1.5} />
      </div>
      <div>
        <div className="text-3xl md:text-4xl font-bold text-white font-heading drop-shadow-sm">
          <AnimatedCounter end={value || 0} />
        </div>
        <div className="text-xs md:text-sm text-white/90 font-medium uppercase tracking-wide mt-1">{label}</div>
      </div>
    </div>
    <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
  </div>
);

// --- CATEGORY CARD (Responsivo e sem bordas duras) ---
const CategoryCard = ({ icon: Icon, name, count, color }) => (
  <Link 
    to={`/vagas?area=${encodeURIComponent(name)}`}
    className="group relative overflow-hidden bg-white dark:bg-slate-800/50 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-2 flex flex-col items-center text-center border border-slate-100 dark:border-transparent"
  >
    <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${color}`} />
    <div className="relative z-10">
      <div className={`w-14 h-14 rounded-2xl ${color} flex items-center justify-center mb-4 group-hover:scale-110 group-hover:bg-white/20 transition-all duration-500 mx-auto shadow-sm`}>
        <Icon className="w-7 h-7 text-white group-hover:text-white" strokeWidth={1.5} />
      </div>
      <h3 className="font-semibold text-lg text-slate-800 dark:text-white group-hover:text-white transition-colors">{name}</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 group-hover:text-white/80 transition-colors mt-1">{count} vagas</p>
    </div>
  </Link>
);

// --- RECENT JOB CARD (Mobile Friendly) ---
const RecentJobCard = ({ job }) => (
  <Link 
    to={`/vagas/${job.id}`}
    className="group relative bg-white dark:bg-slate-800/50 rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 block h-full border border-slate-100 dark:border-transparent"
  >
    <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-lime-500/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative z-10 flex flex-col h-full">
      <div className="flex items-start justify-between mb-3">
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          job.modality === 'remoto' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
          job.modality === 'hibrido' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
          'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
        }`}>
          {job.modality === 'presencial' ? 'Presencial' : job.modality === 'hibrido' ? 'Híbrido' : 'Remoto'}
        </span>
        <Sparkles className="w-5 h-5 text-lime-500 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      
      <h3 className="font-semibold text-lg md:text-xl text-slate-800 dark:text-white mb-2 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors line-clamp-2 min-h-[3.5rem] md:min-h-[auto]">
        {job.title}
      </h3>
      
      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400 mb-4 flex-grow">
        <span className="flex items-center gap-1">
          <Building2 className="w-4 h-4 shrink-0 text-teal-500" />
          <span className="truncate max-w-[120px]">{job.company_name}</span>
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-4 h-4 shrink-0 text-teal-500" />
          <span className="truncate max-w-[120px]">{job.city}</span>
        </span>
      </div>
      
      <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-700/50 mt-auto">
        <span className="font-bold text-teal-600 dark:text-teal-400 text-sm md:text-base">
          {job.salary_min && job.salary_max 
            ? `R$ ${job.salary_min.toLocaleString()} - ${job.salary_max.toLocaleString()}`
            : 'A combinar'
          }
        </span>
        <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-teal-500 group-hover:translate-x-1 transition-all" />
      </div>
    </div>
  </Link>
);

const HomePage = () => {
  const [stats, setStats] = useState({ total_jobs: 0, total_companies: 0, total_hired: 0, total_filled: 0 });
  const [recentJobs, setRecentJobs] = useState([]);
  
  const [categories, setCategories] = useState([
    { name: 'TI', icon: Code, count: 0, color: 'bg-gradient-to-br from-blue-500 to-blue-600' },
    { name: 'Saúde', icon: HeartPulse, count: 0, color: 'bg-gradient-to-br from-red-500 to-pink-500' },
    { name: 'Comércio', icon: ShoppingBag, count: 0, color: 'bg-gradient-to-br from-orange-500 to-amber-500' },
    { name: 'Indústria', icon: Factory, count: 0, color: 'bg-gradient-to-br from-slate-600 to-slate-700' },
    { name: 'Educação', icon: GraduationCap, count: 0, color: 'bg-gradient-to-br from-purple-500 to-violet-600' },
    { name: 'Construção', icon: Hammer, count: 0, color: 'bg-gradient-to-br from-yellow-500 to-orange-500' },
  ]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [statsRes, jobsRes] = await Promise.all([statsAPI.get(), jobsAPI.getAll({})]);
        
        if(statsRes.data) {
            setStats(statsRes.data);
            if (statsRes.data.jobs_by_area) {
              setCategories(prev => prev.map(cat => ({
                ...cat,
                count: statsRes.data.jobs_by_area[cat.name] || 0
              })));
            }
        }

        if (jobsRes.data) {
            setRecentJobs(jobsRes.data.slice(0, 6));
        }

      } catch (err) {
        console.error('Erro ao carregar dados da Home:', err);
      }
    };
    loadData();
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] md:min-h-[95vh] flex items-center py-12 md:py-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-teal-50/30 to-lime-50/20 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800" />
        <div className="absolute top-0 right-0 w-[400px] md:w-[800px] h-[400px] md:h-[800px] bg-gradient-to-br from-teal-400/20 to-lime-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3 animate-float" />
        <div className="absolute bottom-0 left-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-to-tr from-teal-500/10 to-transparent rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
        
        <div className="container mx-auto px-4 md:px-12 lg:px-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="space-y-6 md:space-y-8 animate-in fade-in slide-in-from-left-8 duration-700">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500/10 to-lime-500/10 dark:from-teal-500/20 dark:to-lime-500/20 border border-teal-500/20 text-teal-700 dark:text-teal-400 px-4 py-2 md:px-5 md:py-2.5 rounded-full text-xs md:text-sm font-medium backdrop-blur-sm">
                <MapPin className="w-3.5 h-3.5 md:w-4 md:h-4" />
                <span>Vagas exclusivas em Rondônia</span>
                <span className="w-2 h-2 bg-lime-500 rounded-full animate-pulse ml-1" />
              </div>
              
              {/* --- MUDANÇA PRINCIPAL AQUI --- */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold font-heading leading-tight">
                <span className="text-slate-800 dark:text-white">Sua carreira em</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 via-teal-500 to-lime-500 animate-gradient-x">Rondônia começa aqui</span>
              </h1>
              
              <p className="text-base md:text-xl text-slate-600 dark:text-slate-300 max-w-lg leading-relaxed">
                Encontre as melhores oportunidades de emprego em todo o estado de Rondônia. 
                Empresas e candidatos conectados em um só lugar.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-4 w-full sm:w-auto">
                <Link to="/vagas" className="w-full sm:w-auto">
                  <Button 
                    data-testid="find-jobs-btn"
                    className="w-full sm:w-auto group bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white rounded-full px-8 py-6 md:py-7 text-base md:text-lg font-semibold shadow-xl shadow-teal-500/25 hover:shadow-teal-500/40 hover:scale-105 transition-all duration-300"
                  >
                    <Search className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Encontrar Vagas
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right Content - Stats Grid */}
            <div className="grid grid-cols-2 gap-3 md:gap-4 animate-in fade-in slide-in-from-right-8 duration-700 delay-200">
              <StatCard 
                icon={Briefcase} 
                value={stats.total_jobs} 
                label="Vagas disponíveis" 
                gradient="bg-gradient-to-br from-teal-500 to-teal-600"
                delay={0}
              />
              <StatCard 
                icon={Building2} 
                value={stats.total_companies} 
                label="Empresas cadastradas" 
                gradient="bg-gradient-to-br from-[#003b4a] to-[#004d5a]"
                delay={100}
              />
              <StatCard 
                icon={Users} 
                value={stats.total_hired} 
                label="Pessoas empregadas" 
                gradient="bg-gradient-to-br from-lime-500 to-lime-600"
                delay={200}
              />
              <StatCard 
                icon={CheckCircle} 
                value={stats.total_filled} 
                label="Vagas preenchidas" 
                gradient="bg-gradient-to-br from-teal-600 to-[#003b4a]"
                delay={300}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 md:py-24 bg-slate-50/50 dark:bg-slate-900/50">
        <div className="container mx-auto px-4 md:px-12 lg:px-20">
          <div className="text-center mb-10 md:mb-16">
            <span className="text-teal-600 dark:text-teal-400 font-medium text-xs md:text-sm tracking-wider uppercase">Explore por área</span>
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-slate-800 dark:text-white mt-2 md:mt-3 mb-3 md:mb-4">
              Categorias Populares
            </h2>
            <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Encontre vagas na sua área de atuação
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
            {categories.map((cat, index) => (
              <div key={cat.name} className="animate-in fade-in zoom-in duration-500" style={{ animationDelay: `${index * 50}ms` }}>
                <CategoryCard {...cat} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Jobs Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-12 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 md:mb-16 gap-4">
            <div className="text-center md:text-left">
              <span className="text-teal-600 dark:text-teal-400 font-medium text-xs md:text-sm tracking-wider uppercase">Oportunidades</span>
              <h2 className="text-3xl md:text-5xl font-bold font-heading text-slate-800 dark:text-white mt-2 md:mt-3">
                Vagas Recentes
              </h2>
            </div>
            <Link 
              to="/vagas" 
              className="group flex items-center justify-center md:justify-start gap-2 text-teal-600 dark:text-teal-400 font-semibold hover:gap-3 transition-all p-2 rounded-lg hover:bg-teal-50 dark:hover:bg-slate-800"
            >
              Ver todas as vagas
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {recentJobs.length > 0 ? (
                recentJobs.map((job, index) => (
                  <div key={job.id} className="animate-in fade-in slide-in-from-bottom-8 duration-700" style={{ animationDelay: `${index * 100}ms` }}>
                    <RecentJobCard job={job} />
                  </div>
                ))
            ) : (
                <div className="col-span-full text-center py-16 bg-slate-50 dark:bg-slate-800/30 rounded-2xl border border-dashed border-slate-300 dark:border-slate-700">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 dark:text-slate-400">Nenhuma vaga publicada recentemente.</p>
                </div>
            )}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-teal-50/30 dark:from-slate-900 dark:to-slate-800">
        <div className="container mx-auto px-4 md:px-12 lg:px-20">
          <div className="text-center mb-10 md:mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-heading text-slate-800 dark:text-white mb-4">
              Por que escolher o Empregos RO?
            </h2>
            <p className="text-base md:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              A plataforma mais completa para quem busca ou oferece oportunidades em Rondônia
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {[
              {
                icon: MapPin,
                title: 'Foco Regional',
                description: 'Vagas exclusivas do estado de Rondônia, conectando talentos locais a empresas da região.',
                gradient: 'from-teal-500 to-teal-600'
              },
              {
                icon: TrendingUp,
                title: 'Estatísticas em Tempo Real',
                description: 'Acompanhe métricas e tendências do mercado de trabalho local com dados atualizados.',
                gradient: 'from-lime-500 to-lime-600'
              },
              {
                icon: Users,
                title: 'Conexão Direta',
                description: 'Contato direto entre candidatos e empresas, sem intermediários nem burocracias.',
                gradient: 'from-[#003b4a] to-teal-600'
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className="group relative bg-white dark:bg-slate-800/50 rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden animate-in fade-in slide-in-from-bottom-8"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${feature.gradient}`} />
                <div className={`w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 shadow-lg`}>
                  <feature.icon className="w-7 h-7 md:w-8 md:h-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="text-lg md:text-xl font-semibold font-heading text-slate-800 dark:text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#003b4a] via-teal-600 to-teal-500" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-lime-500/20 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-teal-300/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }} />
        </div>
        
        <div className="container mx-auto px-4 md:px-12 lg:px-20 relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-white mb-4 md:mb-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            Pronto para Divulgar?
          </h2>
          <p className="text-base md:text-xl text-white/80 mb-8 md:mb-10 max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-700 delay-100">
            Divulgue suas vagas, destaque sua empresa e conecte-se com os melhores talentos de Rondônia.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
            <Link to="/contato" className="w-full sm:w-auto">
              <Button 
                data-testid="register-cta-btn"
                className="w-full sm:w-auto group bg-lime-500 hover:bg-lime-400 text-[#003b4a] rounded-full px-8 md:px-10 py-6 md:py-7 text-base md:text-lg font-bold shadow-xl shadow-lime-500/25 hover:shadow-lime-400/40 hover:scale-105 transition-all duration-300"
              >
                Entrar em Contato
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/vagas" className="w-full sm:w-auto">
              <Button 
                data-testid="browse-jobs-cta-btn"
                variant="outline"
                className="w-full sm:w-auto border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 rounded-full px-8 md:px-10 py-6 md:py-7 text-base md:text-lg font-semibold transition-all duration-300"
              >
                Ver Todas as Vagas
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;